import type { BotLevel } from "../constants";
import type { Rng } from "../rng";
import { pick } from "../rng";
import { GameError, type GameEvent, type TurnGameModule, type Winner } from "./types";

/**
 * Ludo – the classic dice race on a cross-shaped board (2–4 players), with the site's own rule:
 * you must answer a question correctly to be allowed to roll the die. A wrong answer simply passes the turn.
 *
 *  - Every player has PIECES pieces. A 6 brings a piece out of the base onto the start square.
 *  - A 6 also gives another turn (with a new question).
 *  - A piece walks 51 squares round the board, then 5 squares up its own coloured home column and finally into the goal (56 steps in all).
 *    It must land on the goal with an exact roll.
 *  - Landing on a square held by another player's piece sends that piece back to its base – except on the start squares and the star squares.
 *  - First player to bring all pieces into the goal wins.
 *  - If all of your pieces sit in the base you get up to three rolls in one turn to find a 6 (so nobody waits forever).
 */

export const PIECES = 2;
export const GOAL = 56;
const RING_LEN = 52;
const LAST_TRACK = 50; // progress 0..50 are squares on the shared ring; 51..55 are the home column; 56 is the goal

type Cell = [number, number];
function buildRing(): Cell[] {
  const r: Cell[] = [];
  const row = (y: number, x0: number, x1: number) => { const st = x1 >= x0 ? 1 : -1; for (let x = x0; x !== x1 + st; x += st) r.push([x, y]); };
  const col = (x: number, y0: number, y1: number) => { const st = y1 >= y0 ? 1 : -1; for (let y = y0; y !== y1 + st; y += st) r.push([x, y]); };
  row(6, 1, 5); col(6, 5, 0); r.push([7, 0]); r.push([8, 0]);
  col(8, 1, 5); row(6, 9, 14); r.push([14, 7]); r.push([14, 8]);
  row(8, 13, 9); col(8, 9, 14); r.push([7, 14]); r.push([6, 14]);
  col(6, 13, 9); row(8, 5, 0); r.push([0, 7]); r.push([0, 6]);
  return r;
}
/** The 52 squares of the shared ring (15×15 grid, clockwise, index 0 = the left arm's start square). */
export const RING: Cell[] = buildRing();
/** Squares of each arm's home column (5 squares) and the goal square. */
export const HOME_COLUMN: Cell[][] = [
  [[1, 7], [2, 7], [3, 7], [4, 7], [5, 7]],
  [[7, 1], [7, 2], [7, 3], [7, 4], [7, 5]],
  [[13, 7], [12, 7], [11, 7], [10, 7], [9, 7]],
  [[7, 13], [7, 12], [7, 11], [7, 10], [7, 9]],
];
export const GOAL_CELL: Cell[] = [[6, 7], [7, 6], [8, 7], [7, 8]];
/** Top-left corner of each arm's base (6×6) and the four spots inside it. */
export const BASE_ORIGIN: Cell[] = [[0, 0], [9, 0], [9, 9], [0, 9]];
export const BASE_SPOTS: Cell[] = [[2, 2], [4, 2], [2, 4], [4, 4]];
export const ARM_OF_SEAT: Record<number, number[]> = { 2: [0, 2], 3: [0, 1, 2], 4: [0, 1, 2, 3] };

/** absolute ring index of a piece that is on the shared ring (progress 0..50), otherwise null */
export const ringIndex = (arm: number, p: number): number | null => (p >= 0 && p <= LAST_TRACK ? (13 * arm + p) % RING_LEN : null);
/** start squares (13·k) and star squares (13·k + 8) are safe */
export const isSafeIndex = (i: number) => i % 13 === 0 || i % 13 === 8;

/** Centre of the square a piece stands on, in board units (the board is 15×15; a square (x,y) has its centre at x+0.5, y+0.5). */
export function pieceXY(arm: number, p: number, piece: number): [number, number] {
  if (p < 0) {
    const o = BASE_ORIGIN[arm]!, s = BASE_SPOTS[piece % 4]!;
    return [o[0] + s[0], o[1] + s[1]];
  }
  if (p <= LAST_TRACK) { const c = RING[(13 * arm + p) % RING_LEN]!; return [c[0] + 0.5, c[1] + 0.5]; }
  if (p < GOAL) { const c = HOME_COLUMN[arm]![p - LAST_TRACK - 1]!; return [c[0] + 0.5, c[1] + 0.5]; }
  const g = GOAL_CELL[arm]!;
  return [g[0] + 0.5 + (piece % 2 ? 0.18 : -0.18), g[1] + 0.5 + (piece >= 2 ? 0.18 : -0.18)];
}

export interface LudoState {
  n: number;
  arms: number[]; // seat -> arm
  pos: number[][]; // pos[seat][piece]: -1 = in the base, 0..55 on the way, 56 = in the goal
  turn: number;
  /** "roll": the seat must answer a question, then the server rolls for it. "move": the seat has rolled and must choose a piece. */
  stage: "roll" | "move";
  dice: number | null;
  legal: number[];
  winner: number | null;
  turns: number;
  last: { seat: number; piece: number; from: number; to: number; dice: number; captured: { seat: number; piece: number }[] } | null;
}
export type LudoAction = { type: "move"; piece: number };

export interface LudoView {
  you: number;
  turn: number;
  stage: "roll" | "move";
  dice: number | null;
  legal: number[];
  pos: number[][];
  arms: number[];
  winner: number | null;
  last: LudoState["last"];
  pieces: number;
  goal: number;
  home: number[]; // pieces in the goal per seat
}

const nextSeat = (s: LudoState, seat: number) => (seat + 1) % s.n;

function canMove(p: number, d: number): boolean {
  if (p < 0) return d === 6;
  if (p >= GOAL) return false;
  return p + d <= GOAL;
}
export function legalPieces(s: LudoState, seat: number, d: number): number[] {
  const out: number[] = [];
  s.pos[seat]!.forEach((p, i) => { if (canMove(p, d)) out.push(i); });
  return out;
}

function movePiece(s: LudoState, seat: number, piece: number, d: number): GameEvent[] {
  const from = s.pos[seat]![piece]!;
  const to = from < 0 ? 0 : from + d;
  s.pos[seat]![piece] = to;
  const captured: { seat: number; piece: number }[] = [];
  const idx = ringIndex(s.arms[seat]!, to);
  if (idx !== null && !isSafeIndex(idx)) {
    for (let o = 0; o < s.n; o++) {
      if (o === seat) continue;
      s.pos[o]!.forEach((p, i) => {
        if (ringIndex(s.arms[o]!, p) === idx) { s.pos[o]![i] = -1; captured.push({ seat: o, piece: i }); }
      });
    }
  }
  s.last = { seat, piece, from, to, dice: d, captured };
  const ev: GameEvent[] = [{ kind: "move", seat, data: { piece, from, to, dice: d } }];
  for (const c of captured) ev.push({ kind: "capture", seat, data: { victim: c.seat, piece: c.piece } });
  if (to === GOAL) ev.push({ kind: "home", seat, data: { piece } });
  if (s.pos[seat]!.every((p) => p === GOAL)) {
    s.winner = seat;
    ev.push({ kind: "win", seat });
  } else if (d === 6) ev.push({ kind: "bonus", seat });
  // a 6 gives another turn (with a new question); everything else passes the die on
  s.stage = "roll";
  s.legal = [];
  s.turns++;
  if (s.winner === null && d !== 6) s.turn = nextSeat(s, seat);
  return ev;
}

function passTurn(s: LudoState, seat: number): void {
  s.stage = "roll";
  s.legal = [];
  s.dice = null;
  s.turn = nextSeat(s, seat);
  s.turns++;
}

/** Rolls for `seat` and either moves automatically (one choice), waits for a choice (several) or passes (none). */
function rollFor(s: LudoState, seat: number, rng: Rng): GameEvent[] {
  const ev: GameEvent[] = [];
  const allHome = s.pos[seat]!.every((p) => p < 0);
  const tries = allHome ? 3 : 1;
  for (let t = 0; t < tries; t++) {
    const d = 1 + Math.floor(rng() * 6);
    s.dice = d;
    ev.push({ kind: "roll", seat, data: { dice: d } });
    const legal = legalPieces(s, seat, d);
    if (legal.length === 1) {
      ev.push(...movePiece(s, seat, legal[0]!, d));
      return ev;
    }
    if (legal.length > 1) {
      s.stage = "move";
      s.legal = legal;
      return ev;
    }
  }
  ev.push({ kind: "nomove", seat });
  passTurn(s, seat);
  return ev;
}

/** how good is moving this piece? (used by the computer players) */
function score(seat: number, piece: number, d: number, view: LudoView): number {
  const from = view.pos[seat]![piece]!;
  const to = from < 0 ? 0 : from + d;
  let v = to; // further is better
  if (to === GOAL) v += 60;
  if (from < 0) v += 40;
  const idx = ringIndex(view.arms[seat]!, to);
  if (idx !== null) {
    let captured = 0;
    let danger = 0;
    for (let o = 0; o < view.pos.length; o++) {
      if (o === seat) continue;
      view.pos[o]!.forEach((p) => {
        const oi = ringIndex(view.arms[o]!, p);
        if (oi === null) return;
        if (oi === idx && !isSafeIndex(idx)) captured++;
        const behind = (idx - oi + RING_LEN) % RING_LEN;
        if (behind >= 1 && behind <= 6) danger++;
      });
    }
    v += captured * 50;
    if (!isSafeIndex(idx)) v -= danger * 6;
    else v += 5;
  }
  const fi = ringIndex(view.arms[seat]!, from);
  if (fi !== null && !isSafeIndex(fi)) v += 3; // leaving an exposed square is nice
  return v;
}

export const ludo: TurnGameModule<LudoState, LudoAction, LudoView> = {
  id: "ludo",
  create(rng, players) {
    const n = Math.max(2, Math.min(4, players));
    return {
      n,
      arms: ARM_OF_SEAT[n]!.slice(),
      pos: Array.from({ length: n }, () => Array.from({ length: PIECES }, () => -1)),
      turn: Math.floor(rng() * n),
      stage: "roll",
      dice: null,
      legal: [],
      winner: null,
      turns: 0,
      last: null,
    };
  },
  phase: (s) => (s.winner !== null ? "over" : "play"),
  turn: (s) => s.turn,
  setupSeats: () => [],
  needsQuestion: (s) => (s.winner === null && s.stage === "roll" ? "roll" : null),
  continues: (s) => s.winner === null && s.stage === "move",

  /** The question outcome: a right answer rolls the die; a wrong one passes the turn. */
  resolve(s, seat, correct, rng) {
    if (s.winner !== null || seat !== s.turn || s.stage !== "roll") return [];
    if (!correct) {
      passTurn(s, seat);
      return [{ kind: "skip", seat }];
    }
    return rollFor(s, seat, rng);
  },

  apply(s, seat, a) {
    if (s.winner !== null) throw new GameError("Spillet er slut.");
    if (seat !== s.turn) throw new GameError("Det er ikke din tur.");
    if (s.stage !== "move" || s.dice === null) throw new GameError("Svar på spørgsmålet først, så slår du terningen.");
    if (a?.type !== "move" || !Number.isInteger(a.piece)) throw new GameError("Vælg en af dine brikker.");
    if (!s.legal.includes(a.piece)) throw new GameError("Den brik kan ikke flyttes med det slag.");
    return movePiece(s, seat, a.piece, s.dice);
  },
  /** (wrong answer is handled by resolve) */
  penalty(s, seat) {
    passTurn(s, seat);
    return [{ kind: "skip", seat }];
  },
  /** Action timeout: the furthest legal piece moves by itself. */
  pass(s, seat) {
    if (s.stage === "move" && s.dice !== null && s.legal.length) {
      const best = s.legal.slice().sort((x, y) => s.pos[seat]![y]! - s.pos[seat]![x]!)[0]!;
      return movePiece(s, seat, best, s.dice);
    }
    passTurn(s, seat);
    return [{ kind: "pass", seat }];
  },
  winner: (s): Winner => s.winner,
  view(s, seat): LudoView {
    const mine = s.turn === seat && s.stage === "move";
    return {
      you: seat,
      turn: s.turn,
      stage: s.stage,
      dice: s.dice,
      legal: mine ? s.legal : [],
      pos: s.pos.map((p) => p.slice()),
      arms: s.arms,
      winner: s.winner,
      last: s.last,
      pieces: PIECES,
      goal: GOAL,
      home: s.pos.map((p) => p.filter((x) => x === GOAL).length),
    };
  },
  bot(view, seat, level: BotLevel, rng): LudoAction {
    const d = view.dice ?? 1;
    const legal = view.legal.length ? view.legal : [0];
    if (level === "easy") return { type: "move", piece: pick(legal, rng) };
    const noise = level === "medium" ? 12 : 0;
    let best = legal[0]!, bestV = -Infinity;
    for (const piece of legal) {
      const v = score(seat, piece, d, view) + (noise ? rng() * noise : 0);
      if (v > bestV) { bestV = v; best = piece; }
    }
    return { type: "move", piece: best };
  },
};
